/**
 * Lead API Module
 *
 * 도입문의(Marketing Lead) 관련 API 호출을 담당하는 모듈
 *
 * @example
 * import { leadApi } from '@new/api'
 *
 * const response = await leadApi.createLead({
 *   name: '테스트 매장',
 *   type: 'STORE',
 *   contact: '010-1234-5678',
 *   // ...
 * })
 */
import { AxiosResponse } from 'axios';
import { apiClient, safeRequest, SafeRequestResult } from '../client';

/**
 * 도입문의 생성 요청 데이터
 */
export interface CreateLeadData {
  name: string; // 업체명
  type: 'STORE' | 'PRODUCT'; // 광고 카테고리
  contact: string; // 연락처
  isMarketingAgreed: boolean; // 마케팅 수신 동의
  x?: number; // 경도
  y?: number; // 위도
  roadAddress?: string; // 도로명 주소
  planMonths?: number; // 이용 예정 개월 (STORE)
  packagePrice?: number; // 패키지 가격 (PRODUCT)
  email?: string; // 이메일 (PRODUCT)
  availableTime?: string; // 통화 가능 시간 (STORE)
}

/**
 * 도입문의 생성 응답 데이터
 */
export interface CreateLeadResponse {
  id: number;
  companyName: string;
  adCategory: string;
  contact: string;
  agreeToMarketing: boolean;
  [key: string]: unknown;
}

/**
 * API 요청 Body 타입 (내부 변환용)
 */
interface CreateLeadRequestBody {
  companyName: string;
  adCategory: string;
  contact: string;
  agreeToMarketing: boolean;
  x?: number;
  y?: number;
  address?: string;
  planMonths?: number;
  packagePrice?: number;
  email?: string;
  availableTime?: string;
}

/**
 * Lead API
 */
export const leadApi = {
  /**
   * 도입문의 생성
   * @param leadData - 도입문의 데이터
   * @returns 생성된 도입문의 정보
   */
  async createLead(leadData: CreateLeadData): Promise<AxiosResponse<CreateLeadResponse>> {
    // leadData를 API 스펙에 맞게 변환
    const requestBody: CreateLeadRequestBody = {
      companyName: leadData.name,
      adCategory: leadData.type,
      contact: leadData.contact,
      agreeToMarketing: leadData.isMarketingAgreed,
      x: leadData.x,
      y: leadData.y,
      ...(leadData.roadAddress && { address: leadData.roadAddress }),
      ...(leadData.type === 'STORE' && leadData.planMonths && { planMonths: leadData.planMonths }),
      ...(leadData.type === 'PRODUCT' && leadData.packagePrice && { packagePrice: leadData.packagePrice }),
      ...(leadData.type === 'PRODUCT' && leadData.email && { email: leadData.email }),
      ...(leadData.type === 'STORE' && leadData.availableTime && { availableTime: leadData.availableTime }),
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🔍 [createLead] API 요청');
      console.log('📍 URL: /marketing-lead');
      console.log('📦 Request Body:', JSON.stringify(requestBody, null, 2));
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }

    try {
      const response = await apiClient.post<CreateLeadResponse>('/marketing-lead', requestBody);

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ [createLead] API 응답:', response.data);
      }

      return response;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('🔴 [createLead] API 에러:', error);
      }
      throw error;
    }
  },

  /**
   * 안전한 도입문의 생성 (에러 래핑)
   * @param leadData - 도입문의 데이터
   * @returns SafeRequestResult 형식의 응답
   */
  async safeCreateLead(leadData: CreateLeadData): Promise<SafeRequestResult<CreateLeadResponse>> {
    return safeRequest(this.createLead(leadData));
  },
};

export default leadApi;
